import React, { Dispatch, ReactNode, useRef, useState } from "react";
import useCache from "../hooks/useCache";
import { useLocation } from "react-router";
import { setCookie } from "../hooks/setCookie";
import { getCookie } from "../hooks/getCookie";
import {
  isNewExternalArrival,
  readHandoffContext,
  shouldSealEntryPage,
} from "../utils/attribution";
import {
  captureArrivalAttribution,
  installAttributionRecapture,
  resolveAttributionValue,
} from "../utils/attributionResolve";
import { applyAffiliateTouch, restoreAffiliateTouch } from "../utils/affiliateTouch";
import { threeMonthsDate } from "../utils/Dates.utils";
import { DOMAIN } from "../constants/Global";
import { GlobalPromoCodesToApply } from "../utils/InternationlChanges";
import { trackCta } from "../helpers/Tracking";
import { NotificationI } from "../constants/Notification";
import { ToastValues } from "get-life-storybook-ts/lib/atoms/Toast/Toast";
import {
  THEME_COLORS,
  ThemeColorsT,
} from "get-life-storybook-ts/lib/constants/GetlifeStyles";

interface ContextProps {
  children: React.ReactChildren;
}

export type AlertT = "success" | "error" | "info";

export type LanguageT = "es" | "fr" | "en" | "pt" | "it" | "wl_es" | "sql_es";

export type UserStatusT =
  | "product_questions_pending"
  | "questions_pending"
  | "rejected"
  | "nationality_pending"
  | "upsell_pending"
  | "payment_pending"
  | "paymentmethodpending_pending"
  | "address_pending"
  | "beneficiaries_pending"
  | "idcard_pending"
  | "beneficiariestypes_pending"
  | "physicalperson_pending"
  | "bankingentity_pending"
  | "legalperson_pending"
  | "legalnote_pending"
  | "signature_pending"
  | "complete"
  | "ops_review"
  | "it_review"
  | "ready_to_send"
  | "insurer_review"
  | "claim"
  | "docs_pending"
  | "csv_pending"
  | "validation_pending"
  | "initial_status"
  | "prestack_pending"
  | "telesubscription"
  | "advisor_pending"
  | "consents_pending"
  | "payment_validation_pending"
  | "canceled";

interface NavbarStickyInfoI {
  actions?: React.ReactNode;
  content?: React.ReactNode;
  position?: "top" | "bottom";
  show?: boolean;
}

interface GetlifeContextI {
  backFlag: boolean;
  brokerId: string;
  domain: LanguageT;
  handleCallMe: () => void;
  init: (entry_point?: string, loading?: boolean) => void;
  isABtesting: boolean;
  lang: LanguageT;
  leadId: string;
  loading: boolean;
  navbarStickyInfo: NavbarStickyInfoI;
  next: string;
  nodeElementAlert: any;
  nodeWeCallYou: any;
  nodeMaxCapitalModal: any;
  notification: NotificationI | undefined;
  openModal: boolean;
  otp: string;
  progressBar: number | boolean;
  showHeader: boolean;
  showFooter: boolean;
  question: string;
  setBackFlag: (value: boolean) => void;
  setBrokerId: (value: string) => void;
  setDomain: (value: LanguageT) => void;
  setIsABtesting: (value: boolean) => void;
  setLang: Dispatch<LanguageT>;
  setLeadId: (value: string) => void;
  setLoading: (value: boolean) => void;
  setNavbarStickyInfo: (value: NavbarStickyInfoI) => void;
  setNext: (value: string) => void;
  setNotification: (value: NotificationI) => void;
  setOpenModal: (value: boolean) => void;
  setOtp: (value: string) => void;
  setProgressBar: (value: number | boolean) => void;
  setQuestion: (value: {}) => void;
  setShowHeader: (value: boolean) => void;
  setShowFooter: (value: boolean) => void;
  setToken: (value: string) => void;
  setTokenVelogica: (value: string) => void;
  setUserStatus: (value: UserStatusT) => void;
  token: string;
  tokenVelogica: string;
  userStatus: UserStatusT;
  gtf_campaign: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  utm_test: string | null;
  tid: string | null;
  gclid: string | null;
  gbraid: string | null;
  wbraid: string | null;
  msclkid: string | null;
  fbclid: string | null;
  ucc: string | null;
  setGtf_campaign: (value: string | null) => void;
  setUtm_source: (value: string | null) => void;
  setUtm_medium: (value: string | null) => void;
  setUtm_campaign: (value: string | null) => void;
  setUtm_content: (value: string | null) => void;
  setUtm_term: (value: string | null) => void;
  setUtm_test: (value: string | null) => void;
  setTid: (value: string | null) => void;
  setGclid: (value: string | null) => void;
  setGbraid: (value: string | null) => void;
  setWbraid: (value: string | null) => void;
  setMsclkid: (value: string | null) => void;
  setFbclid: (value: string | null) => void;
  setUcc: (value: string | null) => void;
  getEntryPage: () => string | null;
  getReferrerUrl: () => string | null;
  getABTestVersion: () => string | null;
  sendEventsNewLead: string | null;
  setSendEventsNewLead: (value: string) => void;
  toast: ToastValues;
  setToast: (values: ToastValues) => void;
  toastIsOpen: boolean;
  setToastIsOpen: (value: boolean) => void;
  setThemeColor: (value: keyof ThemeColorsT | "") => void;
  logo: string | null;
  setLogo: (value: string | null) => void;
  brokerPhone: string | null;
  setBrokerPhone: (value: string | null) => void;
  brokerSchedule: [];
  setBrokerSchedule: (value: []) => void;
}

export const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export const GetlifeContext = React.createContext({} as GetlifeContextI);

export const GetlifeProvider = ({ children }: ContextProps) => {
  const [leadId, setLeadId] = useCache("lead");
  const [token, setToken] = useCache("token");
  const [userStatus, setUserStatus] = useCache("userStatus");
  const [brokerId, setBrokerId] = useCache("brokerId");
  const [ABtesting, setIsABtesting] = useCache("isABtesting");
  const [backFlag, setBackFlag] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [domain, setDomain] = useState<LanguageT>(DOMAIN as LanguageT);
  const [lang, setLang] = useState<LanguageT>("es");
  const [otp, setOtp] = useState<string>("");
  const [question, setQuestion] = useCache("question");
  const [tokenVelogica, setTokenVelogica] = useCache("tokenVelogica");
  const [loading, setLoading] = useState<boolean>(false);
  const [next, setNext] = useState<string>("");
  const [notification, setNotification] = useState<NotificationI>({
    message: "",
  });
  const [toastIsOpen, setToastIsOpen] = useState<boolean>(false);
  const [toast, setToast] = useState<ToastValues>({
    onClose: () => setToastIsOpen(false),
  });
  const [progressBar, setProgressBar] = useState<number | boolean>(false);
  const [showHeader, setShowHeader] = useState<boolean>(true);
  const [showFooter, setShowFooter] = useState<boolean>(true);
  const [navbarStickyInfo, setNavbarStickyInfo] = useState<NavbarStickyInfoI>({
    show: false,
    content: <></>,
    actions: <></>,
    position: "bottom",
  });

  const [gtf_campaign, setGtf_campaign] = useState<string | null>(
    GlobalPromoCodesToApply[DOMAIN as LanguageT]
  );
  // Atribución: URL > sello de sesión > cookie. Ver utils/attributionResolve.
  const [utm_source, setUtm_source] = useState<string | null>(
    resolveAttributionValue("utm_source")
  );
  const [utm_medium, setUtm_medium] = useState<string | null>(
    resolveAttributionValue("utm_medium")
  );
  const [utm_campaign, setUtm_campaign] = useState<string | null>(
    resolveAttributionValue("utm_campaign")
  );
  const [utm_content, setUtm_content] = useState<string | null>(
    resolveAttributionValue("utm_content")
  );
  const [utm_term, setUtm_term] = useState<string | null>(
    resolveAttributionValue("utm_term")
  );
  const [utm_test, setUtm_test] = useState<string | null>(
    resolveAttributionValue("utm_test")
  );
  const [tid, setTid] = useState<string | null>(resolveAttributionValue("tid"));
  const [gclid, setGclid] = useState<string | null>(
    resolveAttributionValue("gclid")
  );
  const [gbraid, setGbraid] = useState<string | null>(
    resolveAttributionValue("gbraid")
  );
  const [wbraid, setWbraid] = useState<string | null>(
    resolveAttributionValue("wbraid")
  );
  const [msclkid, setMsclkid] = useState<string | null>(
    resolveAttributionValue("msclkid")
  );
  const [fbclid, setFbclid] = useState<string | null>(
    resolveAttributionValue("fbclid")
  );
  const [ucc, setUcc] = useState<string | null>(resolveAttributionValue("ucc"));
  const [themeColor, setThemeColor] = useState<string | null>(
    getCookie("themeColor")
  );
  const [logo, setLogo] = useState<string | null>(getCookie("logo"));
  const [brokerPhone, setBrokerPhone] = useState<string | null>(null);
  const [brokerSchedule, setBrokerSchedule] = useState<[]>([]);

  const getEntryPage = () => resolveAttributionValue("entryPage");
  const getReferrerUrl = () => resolveAttributionValue("referrerUrl");
  const getABTestVersion = () => getCookie("ABTestVersion");
  const [sendEventsNewLead, setSendEventsNewLead] =
    useCache("sendEventsNewLead");
  const nodeElementAlert = useRef();
  const nodeWeCallYou = useRef<any>(null);
  const nodeMaxCapitalModal = useRef<any>(null);

  const isABtesting = ABtesting === "true" || ABtesting === true;
  const utms: string[] = [
    "gtf_campaign",
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_content",
    "utm_term",
    "utm_test",
    "tid",
    "gclid",
    "gbraid",
    "wbraid",
    "msclkid",
    "fbclid",
    "ucc",
  ];

  const handleCallMe = () => {
    // Botón «te llamamos» (abre el formulario): telemetría v3.
    trackCta("callback");
    nodeWeCallYou.current?.handleShowPortal();
  };

  const init = (entry_point?: string, loading = false) => {
    setToken("");
    setLeadId("");
    setUserStatus("initial_status");
    setTokenVelogica("");
    setBrokerId("");
    setIsABtesting(false);
    setGtf_campaign(getCookie("gtf_campaign"));
    setUtm_source(resolveAttributionValue("utm_source"));
    setUtm_medium(resolveAttributionValue("utm_medium"));
    setUtm_campaign(resolveAttributionValue("utm_campaign"));
    setUtm_content(resolveAttributionValue("utm_content"));
    setUtm_term(resolveAttributionValue("utm_term"));
    setUtm_test(resolveAttributionValue("utm_test"));
    setTid(resolveAttributionValue("tid"));
    setGclid(resolveAttributionValue("gclid"));
    setGbraid(resolveAttributionValue("gbraid"));
    setWbraid(resolveAttributionValue("wbraid"));
    setMsclkid(resolveAttributionValue("msclkid"));
    setFbclid(resolveAttributionValue("fbclid"));
    setUcc(resolveAttributionValue("ucc"));
    setSendEventsNewLead(false);
  };

  const saveNewEntryPage = () => setCookie("entryPage", window.location.href);

  const saveNewReferrerUrl = () => setCookie("referrerUrl", document.referrer);

  const saveNewFbclid = (newFbclid: string) => {
    setCookie("fbclid", newFbclid, threeMonthsDate);
    setFbclid(newFbclid);
  };

  const saveNewGclid = (newGclid: string) => {
    setCookie("gclid", newGclid, threeMonthsDate);
    setGclid(newGclid);
  };

  const saveNewGbraid = (newGbraid: string) => {
    setCookie("gbraid", newGbraid, threeMonthsDate);
    setGbraid(newGbraid);
  };

  const saveNewWbraid = (newWbraid: string) => {
    setCookie("wbraid", newWbraid, threeMonthsDate);
    setWbraid(newWbraid);
  };

  const saveNewMsclkid = (newMsclkid: string) => {
    setCookie("msclkid", newMsclkid, threeMonthsDate);
    setMsclkid(newMsclkid);
  };

  const saveNewUcc = (newUcc: string) => {
    setCookie("ucc", newUcc, threeMonthsDate);
    setUcc(newUcc);
  };

  const resetCookiesIfNewQS = (query: URLSearchParams) => {
    // Only a genuine external campaign arrival (attribution params in the URL
    // and a referrer that is not life5.*) may replace the landing context.
    // The web → app handoff carries `lead`/`gtf_campaign` and, from now on,
    // the attribution itself; it must never reset what the web captured.
    const hasNewUTMs = isNewExternalArrival(query, document.referrer);

    if (hasNewUTMs) {
      // utm_source/medium/campaign/content/term/test and tid must be preserved
      // here: the explicit re-set blocks later in the useEffect can be skipped by
      // the early return on `getCookie("gtf_campaign")`, which would leave
      // returning visitors with these cookies nulled and submit leads to
      // /lead/track/v2 with utm_source = null. Click-IDs (gclid/gbraid/wbraid/
      // fbclid/msclkid/ucc) were already in the keep-list and stay there.
      const utmsToBeKeeped = [
        "gclid", "gbraid", "wbraid", "fbclid", "msclkid", "ucc",
        "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_test", "tid",
      ];
      utms.forEach((utm) => {
        if (utmsToBeKeeped.includes(utm)) return;
        setCookie(utm, null);
      });
      saveNewEntryPage();
      saveNewReferrerUrl();
    }
  };

  React.useEffect(() => {
    const newQuery = new URLSearchParams(window.location.search);

    resetCookiesIfNewQS(newQuery);

    // Landing context forwarded by the marketing site in the handoff URL. It
    // is the reliable channel: the CMP deletes `entryPage`/`referrerUrl`
    // cookies on the web while consent is pending.
    const handoff = readHandoffContext(newQuery);
    if (handoff.entryPage) setCookie("entryPage", handoff.entryPage);
    if (handoff.referrerUrl) setCookie("referrerUrl", handoff.referrerUrl);

    // Affiliate 30-day window (2026-09-24): save an affiliate arrival (at_gd),
    // clear it on any other new external campaign (last click), or restore it
    // on a direct return once the 6-hour utm cookies are gone. Runs BEFORE the
    // utm reads below so the restored values flow through the usual path.
    applyAffiliateTouch({
      isNewCampaign: isNewExternalArrival(newQuery, document.referrer),
      hasLiveSource: Boolean(resolveAttributionValue("utm_source", newQuery)),
      restore: restoreAffiliateTouch,
    });

    // Seal the app URL as entry page only for real direct landings. When the
    // visitor comes from life5.* the landing was the marketing site: leaving
    // it empty is better than recording the app as the landing page.
    if (shouldSealEntryPage(getEntryPage(), document.referrer)) saveNewEntryPage();
    if (getCookie("referrerUrl") === null) saveNewReferrerUrl();

    let query_gtf_campaign = newQuery.get("gtf_campaign");
    if (query_gtf_campaign) {
      setCookie("gtf_campaign", query_gtf_campaign);
      setGtf_campaign(query_gtf_campaign);
    } else if (!getCookie("gtf_campaign")) {
      setCookie("gtf_campaign", GlobalPromoCodesToApply[DOMAIN as LanguageT]);
    }
    // Antes había aquí un `return` cuando la cookie `gtf_campaign` ya existía.
    // La landing ES la escribe SIEMPRE en el dominio compartido, así que todo
    // lo que sigue (gclid/gbraid/wbraid/utm_*) era código muerto en ES y el
    // lead se creaba con la atribución a null.

    let query_utm_source = newQuery.get("utm_source");
    if (query_utm_source) {
      setCookie("utm_source", query_utm_source);
      setUtm_source(query_utm_source);
    }

    let query_utm_medium = newQuery.get("utm_medium");
    if (query_utm_medium) {
      setCookie("utm_medium", query_utm_medium);
      setUtm_medium(query_utm_medium);
    }

    let query_utm_campaign = newQuery.get("utm_campaign");
    if (query_utm_campaign) {
      setCookie("utm_campaign", query_utm_campaign);
      setUtm_campaign(query_utm_campaign);
    }

    let query_utm_content = newQuery.get("utm_content");
    if (query_utm_content) {
      setCookie("utm_content", query_utm_content);
      setUtm_content(query_utm_content);
    }

    let query_utm_term = newQuery.get("utm_term");
    if (query_utm_term) {
      setCookie("utm_term", query_utm_term);
      setUtm_term(query_utm_term);
    }

    let query_utm_test = newQuery.get("utm_test");
    if (query_utm_test) {
      setCookie("utm_test", query_utm_test);
      setUtm_test(query_utm_test);
    }

    let query_tid = newQuery.get("tid");
    if (query_tid) {
      setCookie("tid", query_tid);
      setTid(query_tid);
    }

    let query_gclid = newQuery.get("gclid");
    if (query_gclid) saveNewGclid(query_gclid);

    let query_gbraid = newQuery.get("gbraid");
    if (query_gbraid) saveNewGbraid(query_gbraid);

    let query_wbraid = newQuery.get("wbraid");
    if (query_wbraid) saveNewWbraid(query_wbraid);

    let query_msclkid = newQuery.get("msclkid");
    if (query_msclkid) saveNewMsclkid(query_msclkid);

    let query_fbclid = newQuery.get("fbclid");
    if (query_fbclid) saveNewFbclid(query_fbclid);

    let query_ucc = newQuery.get("ucc");
    if (query_ucc) saveNewUcc(query_ucc);

    // Sella en sessionStorage todo lo resuelto (URL o cookie) para que el
    // borrado de cookies que hace CookieYes mientras el consentimiento está
    // pendiente no se lleve por delante la atribución, y marca el origen de la
    // sesión cuando se entra por el magic link /login/<token>.
    const attribution = captureArrivalAttribution(newQuery);
    setUtm_source(attribution.utm_source ?? null);
    setUtm_medium(attribution.utm_medium ?? null);
    setUtm_campaign(attribution.utm_campaign ?? null);
    setUtm_content(attribution.utm_content ?? null);
    setUtm_term(attribution.utm_term ?? null);
    setUtm_test(attribution.utm_test ?? null);
    setTid(attribution.tid ?? null);
    setGclid(attribution.gclid ?? null);
    setGbraid(attribution.gbraid ?? null);
    setWbraid(attribution.wbraid ?? null);
    setMsclkid(attribution.msclkid ?? null);
    setFbclid(attribution.fbclid ?? null);
    setUcc(attribution.ucc ?? null);

    // Devuelve las cookies de atribución desde el sello en cuanto el usuario
    // acepta en el banner (la landing hace lo mismo en helpers/attribution.ts).
    installAttributionRecapture();
  }, []);

  React.useEffect(() => {
    if (DOMAIN === "sql_es") {
      setCookie("themeColor", themeColor);
      const rootTheme: HTMLElement | null = document.querySelector(":root");
      if (!themeColor || themeColor === "") {
        rootTheme?.style.removeProperty("--theme-primary");
        rootTheme?.style.removeProperty("--theme-secondary");
        rootTheme?.style.removeProperty("--theme-tertiary");
        rootTheme?.style.removeProperty("--theme-quaternary");
        rootTheme?.style.removeProperty("--theme-fill");
      } else {
        const colors = THEME_COLORS[themeColor as keyof ThemeColorsT];
        if (colors) {
          rootTheme?.style.setProperty("--theme-primary", colors.primary);
          rootTheme?.style.setProperty("--theme-secondary", colors.secondary);
          rootTheme?.style.setProperty("--theme-tertiary", colors.tertiary);
          rootTheme?.style.setProperty("--theme-quaternary", colors.quaternary);
          rootTheme?.style.setProperty("--theme-fill", colors.fill);
        }
      }
    }
  }, [themeColor]);

  React.useEffect(() => {
    setCookie("logo", logo);
    if (DOMAIN === "sql_es") {
      setCookie("brokerPhone", brokerPhone);
    }
  }, [logo, brokerPhone]);

  const values = {
    backFlag,
    brokerId,
    domain,
    lang,
    handleCallMe,
    init,
    isABtesting,
    leadId,
    loading,
    navbarStickyInfo,
    next,
    notification,
    nodeElementAlert,
    nodeWeCallYou,
    nodeMaxCapitalModal,
    openModal,
    otp,
    progressBar,
    question,
    showHeader,
    showFooter,
    sendEventsNewLead,
    setBackFlag,
    setBrokerId,
    setDomain,
    setIsABtesting,
    setLang,
    setLeadId,
    setLoading,
    setNavbarStickyInfo,
    setNext,
    setNotification,
    setOpenModal,
    setOtp,
    setProgressBar,
    setQuestion,
    setSendEventsNewLead,
    setShowHeader,
    setShowFooter,
    setToken,
    setTokenVelogica,
    setUserStatus,
    token,
    tokenVelogica,
    userStatus,
    gtf_campaign,
    utm_source,
    utm_medium,
    utm_campaign,
    utm_content,
    utm_term,
    utm_test,
    tid,
    gclid,
    gbraid,
    wbraid,
    msclkid,
    fbclid,
    ucc,
    setGtf_campaign,
    setUtm_source,
    setUtm_medium,
    setUtm_campaign,
    setUtm_content,
    setUtm_term,
    setUtm_test,
    setTid,
    setGclid,
    setGbraid,
    setWbraid,
    setMsclkid,
    setFbclid,
    setUcc,
    getEntryPage,
    getReferrerUrl,
    getABTestVersion,
    saveNewFbclid,
    saveNewGclid,
    saveNewGbraid,
    saveNewWbraid,
    saveNewMsclkid,
    toast,
    setToast,
    toastIsOpen,
    setToastIsOpen,
    saveNewUcc,
    setThemeColor,
    logo,
    setLogo,
    brokerPhone,
    setBrokerPhone,
    brokerSchedule,
    setBrokerSchedule,
  };
  return (
    <GetlifeContext.Provider value={values}>
      {children as unknown as ReactNode}
    </GetlifeContext.Provider>
  );
};
